import type * as Sentry from "@sentry/react-native";

export type SentryOptions = Pick<
  Sentry.ReactNativeOptions,
  | "enableUserInteractionTracing"
  | "enableAutoSessionTracking"
  | "tracesSampleRate"
  | "attachStacktrace"
  | "attachScreenshot"
  | "attachViewHierarchy"
  | "enableCaptureFailedRequests"
  | "profilesSampleRate"
  | "replaysSessionSampleRate"
  | "replaysOnErrorSampleRate"
>;

/**
 * The replay rates have to be ABSENT, not 0. The native module treats a present key
 * as "replay enabled" — it null-checks the rate rather than comparing it to 0, and
 * only leaves the native rates unset when neither key is passed. A 0 rate therefore
 * still starts the recorder.
 */
const CONTINUOUS_TELEMETRY_OPTIONS = {
  profilesSampleRate: 1.0,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
} as const;

/**
 * Session replay, the JS profiler and full tracing all run continuously, which a TV
 * SoC cannot absorb — it showed up as input latency on almost every frame. Errors and
 * crashes still report; only the always-on sampling is dropped.
 *
 * Node-safe on purpose: `scripts/make-sentry-options.ts` imports this, so nothing here may reach `react-native`.
 */
export const getSentryOptions = ({
  isContinuousTelemetrySupported,
}: {
  isContinuousTelemetrySupported: boolean;
}): SentryOptions => ({
  enableUserInteractionTracing: true,
  enableAutoSessionTracking: true,
  tracesSampleRate: isContinuousTelemetrySupported ? 1.0 : 0.1,
  attachStacktrace: true,
  attachScreenshot: true,
  attachViewHierarchy: true,
  enableCaptureFailedRequests: true,
  ...(isContinuousTelemetrySupported ? CONTINUOUS_TELEMETRY_OPTIONS : {}),
});
